import React, { useEffect, useState } from "react";
import Button from "components/BaseWidgets/button";
import { Steps as AntdSteps, message } from "antd";
import "./style.scss";
import { StepperActive, StepperFilled, StepperInactive } from "assets/icons";
import AlertNotification from "../alertNotification";
import {
  useBasicInfo,
  useCareerDetails,
  useOnboardingDetails,
  usePartnerPreference,
  usePhysicalMerital,
} from "hooks/api/useOnboard";
import dayjs from "dayjs";
import { retrieveToken, storeToken } from "utils/storage";
import Loader from "components/BaseWidgets/Loader";

function Steps({
  steps = [],
  forms = [],
  onDone = () => {},
  onStepChange = () => {},
  onFinalNext = () => {},
  className,
  buttonWrapperClass,
  contentClass,
  showDoneButton,
}) {
  const [current, setCurrent] = useState(0);
  const API_STEP_MAP = [1, 4, 5, 6];

  useEffect(() => {
    storeToken("currentStep", current);
  }, [current]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const stepFromHash = parseInt(hash, 10);
    const indexFromHash = API_STEP_MAP.indexOf(stepFromHash);

    const resolvedStep =
      indexFromHash !== -1
        ? indexFromHash
        : Number(retrieveToken("currentStep")) || 0;

    setCurrent(resolvedStep);
    onStepChange(resolvedStep);
    setTimeout(() => {
      const savedData = retrieveToken(`formStep${resolvedStep}`);
      if (savedData && forms[resolvedStep]) {
        const parsedData = JSON.parse(savedData);

        const transformedData = Object.fromEntries(
          Object.entries(parsedData).map(([key, value]) => {
            if (
              (key.toLowerCase().includes("date") || key === "dob") &&
              typeof value === "string"
            ) {
              return [key, dayjs(value)];
            }
            return [key, value];
          }),
        );

        forms[resolvedStep].setFieldsValue(transformedData);
      }
    }, 0);
  }, []);

  // apis
  const { mutate: submitBasicDetails, isBasicLoading } = useBasicInfo();
  const { mutate: submitPhysicalDetails, isPhysicalLoading } =
    usePhysicalMerital();
  const { mutate: submitCareerDetails, isCareerLoading } = useCareerDetails();
  const { mutate: submitPartnerPreference, isPartnerLoading } =
    usePartnerPreference();
  const {
    mutateAsync: fetchOnboardingDetails,
    isLoading: isOnboardingLoading,
  } = useOnboardingDetails();

  //button disable
  const [isButtonDisable, setIsButtonDisable] = useState(false);

  const next = async () => {
    if (isButtonDisable) return;

    setIsButtonDisable(true);

    try {
      if (forms[current]) {
        await forms[current].validateFields();

        const currentFormValues = forms[current].getFieldsValue();

        const waitForMutation = () =>
          new Promise((resolve, reject) => {
            const onSuccess = () => resolve();
            const onError = (error) => reject(error);

            switch (current) {
              case 0:
                const payload = {
                  ...currentFormValues,
                  dob: dayjs(currentFormValues.dob).format("DD-MM-YYYY"),
                };
                submitBasicDetails(payload, { onSuccess, onError });
                break;
              case 1:
                submitPhysicalDetails(currentFormValues, {
                  onSuccess,
                  onError,
                });
                break;
              case 2:
                submitCareerDetails(currentFormValues, { onSuccess, onError });
                break;
              case 3: {
                const {
                  AgeRange,
                  BodyHeightRange,
                  BodyWeightRange,
                  userReligion,
                  ...filteredValues
                } = currentFormValues;
                submitPartnerPreference(filteredValues, { onSuccess, onError });
                break;
              }
              default:
                resolve();
            }
          });

        // Wait for the mutation to complete before proceeding
        await waitForMutation();

        const nextStep = current + 1;
        if (nextStep === steps.length) {
          onFinalNext();
        } else {
          await loadStepData(nextStep);
          window.history.pushState(null, "", `#${API_STEP_MAP[nextStep]}`);
        }
      }
    } catch (error) {
      if (error?.errorFields?.length) {
        const messages = error.errorFields
          .slice()
          .reverse()
          .map((field) => field.errors.join(", "))
          .filter(Boolean);

        if (messages.length) {
          messages.forEach((msg) => {
            AlertNotification("error", msg);
          });
        } else {
          AlertNotification("error", "Please fix the errors in the form");
        }
      }
    }
    setTimeout(() => {
      setIsButtonDisable(false);
    }, 500);
  };

  useEffect(() => {
    // Load initial data when component mounts
    const hash = window.location.hash.replace("#", "");
    const stepFromHash = parseInt(hash, 10);
    const indexFromHash = API_STEP_MAP.indexOf(stepFromHash);
    const initialStep = indexFromHash !== -1 ? indexFromHash : 0;

    loadStepData(initialStep);
  }, []);

  const loadStepData = async (step) => {
    try {
      if (forms[current]) {
        const currentFormValues = forms[current].getFieldsValue();
        storeToken(`formStep${current}`, JSON.stringify(currentFormValues));
      }

      const response = await fetchOnboardingDetails({
        step: API_STEP_MAP[step],
      });

      setCurrent(step);
      onStepChange(step);

      await new Promise((resolve) => setTimeout(resolve, 0));

      if (forms[step]) {
        switch (step) {
          case 0:
            const basicInfo = response?.basic_info_data || {};
            const interestList = response?.interest || [];
            const personalityList = response?.personality || [];

            if (basicInfo.dob) {
              basicInfo.dob = dayjs(basicInfo.dob);
            }

            const step0Data = {
              ...basicInfo,
              interests: interestList,
              personality: personalityList,
            };

            forms[step].setFieldsValue(step0Data);
            storeToken("formStep0", JSON.stringify(step0Data));
            storeToken("selectedInterests", JSON.stringify(interestList));
            storeToken("selectedPersonality", JSON.stringify(personalityList));
            break;

          case 1:
            const physicalData = response?.physical_marital_data || {};
            const physicalDefaults = {
              is_orphan: "false",
              drink: 2,
              smoke: 2,
            };

            const mergedPhysicalData = {
              ...physicalDefaults,
              ...Object.fromEntries(
                Object.entries(physicalData).filter(
                  ([_, v]) => v !== null && v !== undefined,
                ),
              ),
            };

            forms[step].setFieldsValue(mergedPhysicalData);
            storeToken("formStep1", JSON.stringify(mergedPhysicalData));
            break;

          case 2:
            const careerData = response?.career_data || {};

            forms[step].setFieldsValue(careerData);
            storeToken("formStep2", JSON.stringify(careerData));
            break;

          case 3:
            const partnerData = response || {};

            const partnerDefaults = {
              drink: 2,
              smoke: 2,
              heightRange: [100, 230],
              height: "100-230",
              BodyWeightRange: [30, 150],
              weight: "30-150",
            };

            const processedPartnerData = {
              ...partnerData?.partner_preference,

              AgeRange: partnerData?.partner_preference?.age
                ? partnerData.partner_preference.age.split("-").map(Number)
                : [],

              BodyHeightRange: partnerData?.partner_preference?.height
                ? partnerData.partner_preference.height.split("-").map(Number)
                : [100, 230],

              BodyWeightRange: partnerData?.partner_preference?.weight
                ? partnerData.partner_preference.weight.split("-").map(Number)
                : [30, 150],

              religion:
                partnerData?.religion?.id ||
                partnerData?.partner_preference?.religion ||
                null,

              userReligion: partnerData?.religion?.id || null,
            };

            const cleanedData = Object.fromEntries(
              Object.entries(processedPartnerData).filter(
                ([_, value]) => value !== null,
              ),
            );

            forms[step].setFieldsValue(cleanedData);
            storeToken("formStep3", JSON.stringify(cleanedData));
            break;
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      AlertNotification("error", error.message || "Failed to load step data");
    }
  };

  // Update your prev function to use loadStepData
  const prev = async () => {
    // Save current form data
    if (forms[current]) {
      const currentFormValues = forms[current].getFieldsValue();
      storeToken(`formStep${current}`, JSON.stringify(currentFormValues));
    }

    const prevStep = current - 1;
    await loadStepData(prevStep);
    window.history.pushState(null, "", `#${API_STEP_MAP[prevStep]}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash.replace("#", "");
      const stepFromHash = parseInt(hash, 10);
      const indexFromHash = API_STEP_MAP.indexOf(stepFromHash);

      if (indexFromHash !== -1 && indexFromHash !== current) {
        loadStepData(indexFromHash);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [current]);

  const items = steps.map((item, index) => ({
    key: item.title,
    title: item.title,
    icon:
      index === current ? (
        <StepperActive />
      ) : index < current ? (
        <StepperFilled />
      ) : (
        <StepperInactive />
      ),
  }));

  return (
    <div className="stepper-wrapper">
      <AntdSteps
        className={`primary-stepper ${className}`}
        current={current}
        items={items}
        direction="horizontal"
        responsive={false}
      />
      {isOnboardingLoading ? (
        <div className="loader-wrapper">
          <div>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={`content-wrapper container ${contentClass}`}>
          {steps[current]?.content}
        </div>
      )}
      <div
        className={`btn-wrapper container flex items-end justify-end ${buttonWrapperClass}`}
      >
        {current < steps.length - 1 && (
          <Button
            className={`stepper-btn`}
            variant="primary"
            onClick={next}
            isDisabled={isButtonDisable}
          >
            Next
          </Button>
        )}
        {current === steps.length - 1 && showDoneButton && (
          <Button
            className={`stepper-btn`}
            variant="primary"
            onClick={() => {
              message.success("All steps completed!");
              onDone();
            }}
          >
            Done
          </Button>
        )}
        {current === steps.length - 1 && !showDoneButton && (
          <Button
            loading={
              isPhysicalLoading ||
              isBasicLoading ||
              isPartnerLoading ||
              isCareerLoading
            }
            className={`stepper-btn`}
            variant="primary"
            onClick={next}
            isDisabled={isButtonDisable}
          >
            Next
          </Button>
        )}
        {current > 0 && (
          <Button
            className={`stepper-btn step-btn-secondary`}
            variant="secondary"
            onClick={prev}
          >
            Back
          </Button>
        )}
      </div>
    </div>
  );
}
export default Steps;
